import * as React from 'react';
import styled from '@emotion/styled';
import useDropdownMenu from 'react-accessible-dropdown-menu-hook';
import breakpoints from '../../design-utils/breakpoints';
import {
  DrawerPlacement,
  useGlobalContext,
} from '../../subscriptions/state/GlobalContext';
import Button from '../Button';

export interface IMenuOption {
  text: string;
  action: () => void;
  icon?: string;
  type?: string;
  color?: string;
  productVariantId?: string;
}
interface ButtonComponent {
  onClick?: (e: any) => void;
  children?: string | React.ReactNode;
  label?: string;
  className?: string;
  disabled?: boolean;
}

export interface IActionsMenuProps {
  menuOptions: IMenuOption[];
  ButtonComponent: React.FC<ButtonComponent>;
  buttonClassnames?: string;
  text?: string;
  buttonAriaLabel?: string;
  buttonDisabled?: boolean;
  isEditable?: boolean;
  isRemovable?: boolean;
}

const DrawerMenuOptions = ({ menuOptions }) => {
  const productVariantId = menuOptions.find(
    ({ productVariantId }) => productVariantId,
  )?.productVariantId;
  return (
    <ul className={productVariantId}>
      {menuOptions.map(({ text, action, color }) => (
        <DrawerButtonContainer className="bundle-button-container" key={text}>
          <DrawerButtonListItem
            className="bundle-button-list-item"
            color={color}
            onClick={action}
          >
            {text}
          </DrawerButtonListItem>
        </DrawerButtonContainer>
      ))}
    </ul>
  );
};

const ActionsMenu = ({
  menuOptions,
  buttonClassnames,
  ButtonComponent,
  text,
  buttonAriaLabel,
  isEditable = true,
  isRemovable = true,
  buttonDisabled = false,
}: IActionsMenuProps): JSX.Element => {
  const { buttonProps, itemProps, isOpen } = useDropdownMenu(
    menuOptions.length,
  );

  const { onDrawerOpen } = useGlobalContext();

  const handleOpenDrawer = () =>
    onDrawerOpen(<DrawerMenuOptions menuOptions={menuOptions} />, {
      placement: DrawerPlacement.bottom,
      withCloseButton: false,
    });

  const handleClick = (e: any) => {
    // If menu only have one option call the action right away
    // without showing the popup
    if (menuOptions.length === 1 && menuOptions[0].type !== 'remove') {
      return menuOptions[0].action();
    }

    window.innerWidth < parseInt(breakpoints.md.replace('px', ''))
      ? handleOpenDrawer()
      : buttonProps?.onClick && buttonProps.onClick(e);
  };

  return (
    <ActionsContent className="bundle-action-list-container">
      {isEditable || isRemovable ? (
        <ButtonComponent
          {...buttonProps}
          disabled={buttonDisabled}
          className={buttonClassnames}
          label={buttonAriaLabel}
          onClick={handleClick}
        >
          {text}
        </ButtonComponent>
      ) : null}
      <ActionsList
        className={`bundle-action-list${isOpen ? ' visible' : ''}`}
        role="menu"
      >
        {menuOptions.map((menuOption: IMenuOption, index: number) => (
          <ActionListItem
            className={`bundle-action-item bundle-action-item--${menuOption.type}`}
            key={menuOption.text}
            {...itemProps[index]}
            onClick={(e) => {
              e.preventDefault();
              menuOption.action();
            }}
            color={menuOption.color}
            href=""
          >
            {menuOption.text}
          </ActionListItem>
        ))}
      </ActionsList>
    </ActionsContent>
  );
};

export default ActionsMenu;

export const EditButton = React.forwardRef(
  ({ onClick, children, disabled }: ButtonComponent, ref) => (
    <BaseEditButton
      className="bundle-action-dots awtomatic-line-edit-button"
      onClick={onClick}
      data-testid="product-options-overflow-menu"
      outlined
      disabled={disabled}
    >
      {children}
    </BaseEditButton>
  ),
);

const BaseEditButton = styled(Button)`
  float: right;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  min-width: fit-content;
`;

const ActionsContent = styled.div`
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  position: relative;
`;

const DrawerButtonListItem = styled.button`
  border: none;
  background: none;
  padding: 24px;
  color: ${({ color }) => color};
  width: 100%;
  text-align: left;
`;

const DrawerButtonContainer = styled.li`
  list-style: none;
`;

const ActionsList = styled.div`
  background-color: white;
  border: 1px solid #dfe3e8;
  display: flex;
  flex-direction: column;
  left: -105px;
  min-height: 38px;
  min-width: 180px;
  position: absolute;
  transition: background-color 0.2s ease-in;
  z-index: 10;
`;

const ActionListItem = styled.a`
  padding: 8px 20px;
  list-style: none;
  cursor: pointer;
  color: ${({ color }) => color};
  &:hover {
    background-color: #f8f8f8;
  }
`;
